import Icon from '@ui/components/atoms/icon/icon.atom';
import withFormController from '@ui/hocs/with-form-controller/with-form-controller.hoc';
import { clsxm } from '@ui/utils/style.util';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import Dropdown, { DropdownProps } from '../dropdown/dropdown.organism';

// #region Types
export type SelectProps<FieldType extends FieldValues> = DropdownProps<FieldType>;
// #endregion

function BaseSelect<FieldType extends FieldValues>({
  className,
  fieldState,
  helperText: passedHelperText,
  label,
  withErrorMessageHelper,
  ...rest
}: SelectProps<FieldType>) {
  // #region Computed Values
  const { error: fieldError, invalid: isInvalid } = fieldState || {};
  const helperText = useMemo(
    () => passedHelperText ?? (withErrorMessageHelper && isInvalid ? fieldError?.message : ''),
    [passedHelperText, fieldError, isInvalid, withErrorMessageHelper],
  );
  // #endregion

  return (
    <Dropdown<FieldType>
      {...rest}
      className={clsxm(
        'h-fit py-[5px] px-2.5 rounded-[5px] bg-white shadow-none border border-grey-input-border',
        !label && (!isInvalid || !withErrorMessageHelper || !helperText) && '!mt-0',
        isInvalid && ['bg-red-light border-red-deep'],
        className,
      )}
      fieldState={fieldState}
      helperText={helperText}
      IconComponent={Icon.ArrowDown}
      label={label}
      withErrorMessageHelper={withErrorMessageHelper}
    />
  );
}
BaseSelect.displayName = 'Select';

const Select = memo(withFormController(BaseSelect)) as typeof BaseSelect;

export default Select as typeof BaseSelect;
